// Profile menu in the top bar: the user's picture (or initials) opens a menu with who is signed in, the current
// tenant, "Switch tenant" and "Sign out". ARIA menu button pattern: Enter, Space and the arrow keys open it;
// arrow keys, Home and End move; Escape closes it and returns focus to the button; Tab and an outside click
// close it.
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import type { Plan, Role } from "../api/types";
import type { AuthUser } from "../auth/auth";
import { planName } from "../lib/plans";
import { routeHash } from "../lib/router";

export interface CurrentTenant {
  slug: string;
  name: string;
  role: Role;
  /** Optional, so a caller without the tenant detail can still give the name and role. */
  plan?: Plan;
  trialEndsAt?: string | null;
}

/** "acme: owner · Trial until 2026-12-02": the caller's role and the plan of the current tenant. */
export function tenantLine(t: CurrentTenant): string {
  const parts = [`${t.slug}: ${t.role}`];
  if (t.plan) {
    const name = planName(t.plan);
    parts.push(t.plan === "trial" && t.trialEndsAt ? `${name} until ${t.trialEndsAt.slice(0, 10)}` : name);
  }
  return parts.join(" · ");
}

/** Up to two initials from the display name, else from the email (before the "@"). "?" if neither. */
export function initials(user: Pick<AuthUser, "displayName" | "email">): string {
  const name = user.displayName?.trim();
  if (name) {
    const words = name.split(/\s+/).filter(Boolean);
    const first = words[0]![0]!;
    const last = words.length > 1 ? words[words.length - 1]![0]! : "";
    return (first + last).toUpperCase();
  }
  const local = user.email?.split("@")[0]?.trim();
  if (local) {
    const parts = local.split(/[._+-]+/).filter(Boolean);
    const first = parts[0]?.[0] ?? local[0]!;
    const second = parts.length > 1 ? parts[1]![0]! : "";
    return (first + second).toUpperCase();
  }
  return "?";
}

export function Avatar({ user }: { user: AuthUser }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [user.photoURL]);
  if (user.photoURL && !failed) {
    return (
      <img
        className="avatar"
        src={user.photoURL}
        alt=""
        width={32}
        height={32}
        // googleusercontent.com often refuses image requests that send a referrer.
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className="avatar avatar-initials" aria-hidden="true">
      {initials(user)}
    </span>
  );
}

export function ProfileMenu({
  user,
  tenant,
  onSignOut,
}: {
  user: AuthUser;
  tenant: CurrentTenant | null;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const pendingFocus = useRef<"first" | "last" | null>(null);
  const menuId = useId();
  const buttonId = useId();
  const who = user.displayName?.trim() || user.email || "your account";

  const items = () => itemRefs.current.filter((e): e is HTMLElement => e !== null);

  function openMenu(focus: "first" | "last") {
    pendingFocus.current = focus;
    setOpen(true);
  }

  function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }

  // Move focus into the menu after it renders.
  useEffect(() => {
    if (!open || !pendingFocus.current) return;
    const list = items();
    (pendingFocus.current === "first" ? list[0] : list[list.length - 1])?.focus();
    pendingFocus.current = null;
  }, [open]);

  // Close on a click or tap outside.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent | MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function onButtonKey(e: KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openMenu("first");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      openMenu("last");
    }
  }

  function onMenuKey(e: KeyboardEvent) {
    const list = items();
    const i = list.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => list[(n + list.length) % list.length]?.focus();
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        go(i + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        go(i - 1);
        break;
      case "Home":
        e.preventDefault();
        go(0);
        break;
      case "End":
        e.preventDefault();
        go(list.length - 1);
        break;
      case "Escape":
        e.preventDefault();
        close(true);
        break;
      case "Tab":
        close(false);
        break;
    }
  }

  return (
    <div className="profile" ref={rootRef}>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        className="profile-button"
        aria-label={`Account menu for ${who}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : openMenu("first"))}
        onKeyDown={onButtonKey}
      >
        <Avatar user={user} />
      </button>
      {open && (
        <div className="profile-popup" onKeyDown={onMenuKey}>
          <div className="profile-head">
            <Avatar user={user} />
            <div className="profile-who">
              {user.displayName?.trim() && <strong>{user.displayName.trim()}</strong>}
              {user.email && <span className="small">{user.email}</span>}
              <span className="small muted">
                {user.passwordAccount ? "Signed in with email" : "Signed in with Google"}
              </span>
            </div>
          </div>
          {tenant && (
            <p className="profile-tenant small" id={`${menuId}-tenant`}>
              <span className="muted">Tenant</span> <strong>{tenant.name}</strong>
              <span className="profile-tenant-line" data-testid="tenant-line">
                {tenantLine(tenant)
                  .split(" · ")
                  .map((part, i) => (
                    <span key={part} className="nowrap">
                      {i > 0 ? " · " : ""}
                      {part}
                    </span>
                  ))}
              </span>
            </p>
          )}
          <ul
            id={menuId}
            role="menu"
            aria-labelledby={buttonId}
            aria-describedby={tenant ? `${menuId}-tenant` : undefined}
            className="profile-items"
          >
            <li role="none">
              <a
                ref={(e) => {
                  itemRefs.current[0] = e;
                }}
                role="menuitem"
                tabIndex={-1}
                href={routeHash({ name: "tenants" })}
                onClick={() => close(false)}
              >
                Switch tenant
              </a>
            </li>
            <li role="none">
              <button
                ref={(e) => {
                  itemRefs.current[1] = e;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                onClick={() => {
                  close(false);
                  onSignOut();
                }}
              >
                Sign out
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
